import React, { useEffect, useState }                from 'react';
import { useRouter }                                 from 'next/router';
import { Button }                                    from '@mantine/core';
import { useForm }                                   from '@mantine/form';
import { CountriesList }                             from '@core/data/countries-list';
import { SUCCESS }                                   from '@core/constants/app-consts';
import { InputField }                                from '@core/components/form/skin-active/InputField';
import { EmailField }                                from '@core/components/form/skin-active/EmailField';
import { CountryField }                              from '@core/components/form/skin-active/CountryField';
import { PhoneNumberField }                          from '@core/components/form/skin-active/PhoneNumberField';
import { MessageField }                              from '@core/components/form/skin-active/MessageField';
import { AppRoutes }                                 from '@core/router/AppRoutes';
import { empty, validateEmail, validateInputLength } from '@core/utils';
import useNotification, { NotificationType }         from '@hooks/useNotification';
import { InquiryInput, useSendInquiryEmailMutation } from '../../../../generated/graphql';
import useStyles                                     from './ContactUsForm.styles';


interface FormValues {
    name: string;
    email: string;
    country: string;
    phoneNumber: string;
    phoneNumberCallingCode: string;
    message: string;
}

export const ContactUsForm: React.FC = () => {

    const { classes } = useStyles();
    const router = useRouter();

    const [countryValue, setCountryValue] = useState('');
    const [callingCodes, setCallingCodes] = useState([]);

    // CALL
    const { notify } = useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [callError, setCallError] = useState('');
    const [sendInquiryEmailMutation] = useSendInquiryEmailMutation();

    const validateForm = (values) => {

        return {
            name:
                empty(values.name)
                    ? 'Name is required'
                    : validateInputLength(values.name, 3, 'Name is too short'),

            email:
                empty(values.email)
                    ? 'Email is required'
                    : validateEmail(values.email, 'Please input valid email address.'),

            country:
                empty(values.country)
                    ? 'Country is required'
                    : null,

            phoneNumber:
                empty(values.phoneNumber)
                    ? 'Phone number is required'
                    : validateInputLength(values.phoneNumber, 7, 'Please input a valid phone number'),

            phoneNumberCallingCode:
                empty(values.phoneNumberCallingCode)
                    ? 'Calling code is required'
                    : null,

            message:
                empty(values.message)
                    ? 'Message is required'
                    : validateInputLength(values.message, 7, 'Message is too short'),
        };
    };

    const form = useForm<FormValues>({

        initialValues: {
            name: '',
            email: '',
            country: '',
            phoneNumber: '',
            phoneNumberCallingCode: '',
            message: ''
        },

        validateInputOnChange: [
            'name',
            'email',
            'country',
            'phoneNumber',
            'phoneNumberCallingCode',
            'message'
        ],

        validate: (values) => {

            setCountryValue(values.country);

            return validateForm(values);

        },

    });

    useEffect(() => {

        if (countryValue) {

            const allCountries = CountriesList.flat();
            const selectedCountry = allCountries.filter((item) => item.label === countryValue);

            setCallingCodes(selectedCountry?.[0]?.callingCodes);

            if (selectedCountry.length) {
                form.setFieldValue('phoneNumberCallingCode', selectedCountry?.[0].callingCodes[0]);
            }
        }

    }, [countryValue]);

    const handleSubmit = async (values: typeof form.values) => {

        //PREPARE THE RECORD HERE
        const record: InquiryInput = {
            name: values.name,
            email: values.email,
            country: values.country,
            phone: `+${values.phoneNumberCallingCode}-${values.phoneNumber}`,
            message: values.message
        };

        // console.log(JSON.stringify(record, null, 2), 'onSubmit_');

        setIsLoading(true);

        await sendInquiryEmailMutation({ variables: { input: record } })
            .then(async ({ data }) => {

                // console.log('D>> ', data);

                if (data.sendInquiryEmail.status === SUCCESS) {

                    setCallError('');
                    setIsLoading(false);

                    /*notify(
                        NotificationType.SUCCESS,
                        `${data.sendInquiryEmail.data}`
                    );*/

                    router.push(AppRoutes.RESPONSE).catch(console.error)

                }

                if (data.sendInquiryEmail.error) {
                    // message.error("Error " + data.sendInquiryEmail.error)

                    setCallError(data.sendInquiryEmail.error);
                    setIsLoading(false);

                    notify(
                        NotificationType.ERROR,
                        `${data.sendInquiryEmail.error}`,
                    );

                }

            })
            .catch((error) => {
                console.error('sendInquiryEmail', error);

                setCallError(error);
                setIsLoading(false);

                notify(
                    NotificationType.ERROR,
                    `Error unable to reach server ${error}`,
                    false
                );

            });

    };

    return (
        <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>

            <InputField
                form={form}
                variable="name"
                fieldLabel="Please enter your full name"
                placeholder="example: John Doe"
                description="Make sure it matches the name on your passport."
            />

            <EmailField
                form={form}
                variable="email"
                fieldLabel="Please enter your email address"
                placeholder="example@gmail.com"
                description="We’ll email you regarding your trip."
            />

            <CountryField
                form={form}
                variable="country"
                fieldLabel="Country"
                placeholder="You can type or use the dropdown"
                description="Please select your country"
                searchable={true}
                clearable={true}
            />

            <PhoneNumberField
                form={form}
                variable="phoneNumber"
                subCompVariable="phoneNumberCallingCode"
                subCompData={callingCodes}
                fieldLabel="Number"
                description="Please enter your number"
                searchable={true}
                clearable={true}
            />

            <MessageField
                form={form}
                variable="message"
                fieldLabel="Message"
                placeholder="I would like to get more information on..."
                description="Please enter your message"
            />

            <Button variant="filled" className={classes.submitButton} radius="xs" type="submit" loading={isLoading} fullWidth={true} mt="xl">
                Submit
            </Button>

        </form>
    );

};
